import type { RumEvent } from '@datadog/browser-rum';

/**
 * Browser extensions run in the page and their exceptions reach our global error
 * handler, so RUM attributes them to us. Their stacks are entirely extension URLs.
 */
const EXTENSION_STACK_SCHEMES = [
  'chrome-extension://',
  'moz-extension://',
  'safari-extension://',
  'safari-web-extension://',
  'ms-browser-extension://',
  'webkit-masked-url://',
];

/**
 * ResizeObserver loop notifications are a browser signal that layout took more than
 * one frame to settle, not an error, and nothing downstream is affected. A bare
 * "Script error." is a cross-origin exception the browser refuses to describe, so
 * there is nothing to act on.
 */
const IGNORED_MESSAGE_PATTERNS = [/^(Uncaught )?"?ResizeObserver loop/, /^(Uncaught )?"?Script error\.?"?$/];

/**
 * Form validation surfaced to the user as intended. Only handled ones: an unhandled
 * ValidationError means something threw where we did not expect it, which we want.
 */
const isIntentionalValidationError = (error: { type?: string; handling?: string }): boolean =>
  error.type === 'ValidationError' && error.handling === 'handled';

const isFromBrowserExtension = (stack?: string): boolean =>
  Boolean(stack) && EXTENSION_STACK_SCHEMES.some((scheme) => stack!.includes(scheme));

/**
 * True when the event is noise we should not send to Error Tracking. Extension
 * errors alone were 59% of frontend error volume as of 2026-08-20, which made the
 * tracker unreadable.
 */
export const shouldDiscardRumEvent = (event: RumEvent): boolean => {
  if (event.type !== 'error') {
    return false;
  }

  const error = (
    event as RumEvent & { error?: { message?: string; stack?: string; type?: string; handling?: string } }
  ).error;

  if (!error) {
    return false;
  }

  if (isFromBrowserExtension(error.stack)) {
    return true;
  }

  if (isIntentionalValidationError(error)) {
    return true;
  }

  return IGNORED_MESSAGE_PATTERNS.some((pattern) => pattern.test(error.message ?? ''));
};
